<script lang="ts">
import { getContext } from 'svelte';
import { building } from '$app/environment';
import { replaceState } from '$app/navigation';
import { page } from '$app/state';
import Link from '../../atoms/Link/Link.svelte';
import Select from '../../atoms/Select/Select.svelte';

interface FilterState {
    industry: string;
    services: string;
    [key: string]: string;
}

interface SelectOption {
    id: string;
    name: string;
}

interface Props {
    industryOptions?: string[];
    servicesOptions?: string[] | SelectOption[];
    filterArray?: FilterState;
}

let {
    industryOptions = [],
    servicesOptions,
    filterArray = $bindable<FilterState>({ industry: '', services: '' }),
}: Props = $props();

// During build/prerendering, searchParams throws an error - use empty URLSearchParams
let currentSearchParams: URLSearchParams;
try {
    currentSearchParams = building ? new URLSearchParams() : page?.url.searchParams;
} catch {
    currentSearchParams = new URLSearchParams();
}

const theServices = getContext<string[] | undefined>('theServices');
const resolvedServicesOptions = $derived(servicesOptions ?? theServices ?? []);

const onChange = (e: Event) => {
    const target = e.target as HTMLInputElement;
    filterArray[target.name] = target.value;
    const url = new URLSearchParams(currentSearchParams);
    if (target.value === '') {
        url.delete(target.name);
    } else {
        url.set(target.name, target.value);
    }
    currentSearchParams = url;
    replaceState(`?${url}`, history.state);
};

const reset = () => {
    filterArray = { industry: '', services: '' };
    currentSearchParams = new URLSearchParams('');
    replaceState('?', history.state);
};
</script>

<div class="filter">
    <Link class="link" type="button" onClick={reset}>
        {#snippet text()}
            Reset All
        {/snippet}
    </Link>
    <Select
        placeholder="Industry"
        selected={filterArray.industry}
        options={industryOptions.sort()}
        onchange={onChange}
        name="industry"
        ariaLabel="Filter by industry"
    />
    <Select
        placeholder="Services"
        selected={filterArray.services}
        options={resolvedServicesOptions}
        onchange={onChange}
        name="services"
        ariaLabel="Filter by service"
    />
</div>

<style lang="scss">
    @use '/src/scss/global-constants/fonts';
    @use '/src/scss/global-constants/mq-config';
    @use 'sass-mq/mq' as *;

    .filter {
        padding: 2rem var(--layout-pagegrid-margin) 3rem;

        @include mq($from: tablet) {
            display: flex;
            flex-direction: row;
            gap: 4.4rem;
            padding: var(--layout-pagegrid-margin) var(--layout-pagegrid-margin) 6.4rem;
        }

        :global(button) {
            font-family: fonts.$fonts-secondary;
            font-size: 1.4rem;
            font-weight: fonts.$fonts-weight--normal;
            line-height: 1.6;
            padding: 0;
            text-align: left;

            @include mq($until: tablet) {
                margin-bottom: 1.3rem;
            }
        }

        :global(.custom-select:not(:last-child)) {
            @include mq($until: tablet) {
                margin-bottom: 2.4rem;
            }
        }
    }
</style>
